import * as AF from "@/components/form-items/AbstractForm";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import { MultilingualField } from "@/components/form-items/MultilingualField";
import MediaLibrary, { ImageFormData } from "@/components/images/MediaLibrary";
import StyledDrawer from "@/components/StyledDrawer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SeparatorWithText } from "@/components/ui/separator";
import { SupportedLocale } from "@/i18n";
import {
  attachmentsToGalleryImages,
  CopyFromMasterConfig,
  galleryIndexForAttachment,
  getDefaultAttachment,
  getDefaultImage,
  POSITION_MAP_SCULPTURE_EDITION_TO_EDITION,
} from "@/lib/image";
import { Attachment } from "@/lib/types";
import { ImageIcon } from "lucide-react";
import { useMemo } from "react";
import { UniqueImageFullScreen } from "../../modal/UniqueImageFullScreen";

type SculptureEditionInformationsTabProps = {
  editionId: string;
  masterArtworkId?: string;
  defaultEditionImage?: string;
  editionAttachments?: unknown[];
  isMediaDrawerOpen: boolean;
  setIsMediaDrawerOpen: (open: boolean) => void;
  handleInvalidateAttachments: () => void;
  computeEditionImageName: (data?: ImageFormData) => string;
  t: (key: string) => string;
};

const SculptureEditionInformationsTab = ({
  editionId,
  masterArtworkId,
  defaultEditionImage,
  editionAttachments,
  isMediaDrawerOpen,
  setIsMediaDrawerOpen,
  handleInvalidateAttachments,
  computeEditionImageName,
  t,
}: SculptureEditionInformationsTabProps) => {
  const copyFromMaster: CopyFromMasterConfig | undefined = masterArtworkId
    ? {
        masterDocId: masterArtworkId,
        masterPositionType: "sculptureEdition",
        positionMap: POSITION_MAP_SCULPTURE_EDITION_TO_EDITION,
        fallbackPosition: "sculptureEditionDescriptionPhotoAutre",
      }
    : undefined;

  const editionAttachmentsList = editionAttachments as
    | Attachment[]
    | undefined;

  const editionPreviewImage = useMemo(
    () =>
      editionAttachmentsList
        ? getDefaultImage(editionAttachmentsList, "sculptureEdition")
        : defaultEditionImage,
    [editionAttachmentsList, defaultEditionImage],
  );

  const editionGalleryImages = useMemo(
    () => attachmentsToGalleryImages(editionAttachmentsList),
    [editionAttachmentsList],
  );

  const editionGalleryInitialIndex = useMemo(() => {
    if (!editionAttachmentsList?.length || editionGalleryImages.length === 0) {
      return 0;
    }
    const defaultAttachment = getDefaultAttachment(
      editionAttachmentsList,
      "sculptureEdition",
    );
    return defaultAttachment
      ? galleryIndexForAttachment(editionGalleryImages, defaultAttachment.id)
      : 0;
  }, [editionAttachmentsList, editionGalleryImages]);

  return (
    <AF.Row className="items-start">
      <AF.Col className="w-1/2">
        {editionId && (
          <div className="mb-6 flex items-end justify-end w-full">
            <StyledDrawer
              title={t(
                "sculpture.form.titreModalGestionImagesSculptureEdition",
              )}
              maxWidth="sm:max-w-5xl"
              sheetProps={{
                open: isMediaDrawerOpen,
                onOpenChange: setIsMediaDrawerOpen,
              }}
            >
              <MediaLibrary
                id={editionId}
                type="sculptureEdition"
                invalidateOnChange={handleInvalidateAttachments}
                computeImageName={computeEditionImageName}
                embedAttachmentOnSubmit={true}
                embeddedInDrawer
                copyFromMaster={copyFromMaster}
                setImageMode={(mode) => {
                  if (!mode) {
                    setIsMediaDrawerOpen(false);
                  }
                }}
              />
            </StyledDrawer>

            <div className="flex items-end justify-end w-full relative">
              {editionPreviewImage && (
                <UniqueImageFullScreen
                  media={{
                    url: editionPreviewImage,
                    largeUrl: editionPreviewImage,
                    alt: "",
                    className:
                      "w-full max-h-[400px] object-contain object-right",
                  }}
                  gallery={
                    editionGalleryImages.length > 0
                      ? editionGalleryImages
                      : undefined
                  }
                  initialIndex={editionGalleryInitialIndex}
                />
              )}
              {!editionPreviewImage && (
                <div
                  className="w-full h-[300px] bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-slate-400 hover:bg-slate-100 transition-colors"
                  onClick={() => setIsMediaDrawerOpen(true)}
                >
                  <ImageIcon className="w-12 h-12 text-slate-400" />
                  <p className="text-sm text-slate-500 font-medium">
                    {t("sculpture.form.thumbnailManageImages_action")}
                  </p>
                </div>
              )}
              <Button
                variant="white"
                className="absolute bottom-2 right-2 text-gray-950 text-xs sm:text-sm flex gap-2 bg-blue-50 hover:bg-blue-100 border border-slate-200"
                onClick={() => setIsMediaDrawerOpen(true)}
              >
                {t("sculpture.form.formulaireBannierePhotosBouton")}
                <Badge variant="outlined">
                  {editionAttachments?.length ?? "0"}
                </Badge>
              </Button>
            </div>
          </div>
        )}

        <AF.Text
          name="sculptureEditionNumeroEnregistrement"
          label={t("sculpture.form.sculptureEditionNumeroEnregistrement")}
        />
        <AF.Text
          name="sculptureEditionNumeroInventaire"
          label={t("sculpture.form.sculptureEditionNumeroInventaire")}
        />
        <AF.Text
          name="sculptureEditionAutreNumero"
          label={t("sculpture.form.sculptureEditionAutreNumero")}
        />
        <AF.Text
          name="sculptureEditionAncienNumero"
          label={t("sculpture.form.sculptureEditionAncienNumero")}
        />

        <ModifiableList
          name="sculptureEditionStatus"
          listKey="liste-status"
          label={t("sculptureEditionStatus")}
        />

        <ModifiableList
          name="sculptureEditionTypeEdition"
          listKey="liste-type-edition-sculpture"
          label={t("sculpture.form.sculptureEditionTypeEdition")}
        />

        <AF.Text
          name="sculptureEditionDescriptionSocle"
          label={t("sculpture.form.sculptureEditionDescriptionSocle")}
        />
        <AF.Text
          name="sculptureEditionDateRealisation"
          label={t("sculpture.form.sculptureEditionDateRealisation")}
        />
        <ModifiableList
          name="sculptureEditionFondeurFabricant"
          listKey="liste-fondeur-fabricant-sculpture"
          label={t("sculpture.form.sculptureEditionFondeurFabricant")}
        />
        <AF.Text
          name="sculptureEditionNumeroFonte"
          label={t("sculpture.form.sculptureEditionNumeroFonte")}
        />

        <MultilingualField
          render={(lang: SupportedLocale) => (
            <AF.Col>
              <ModifiableList
                listKey="liste-technique-sculpture"
                name={`sculptureEditionTechnique.${lang}`}
                label={t("sculpture.form.sculptureEditionTechnique")}
                lang={lang}
              />
              <ModifiableList
                listKey="liste-materiau-sculpture"
                name={`sculptureEditionMateriau.${lang}`}
                label={t("sculpture.form.sculptureEditionMateriau")}
                lang={lang}
              />
              <ModifiableList
                listKey="liste-support-sculpture"
                name={`sculptureEditionSupport.${lang}`}
                label={t("sculpture.form.sculptureEditionSupport")}
                lang={lang}
              />
              <ModifiableList
                listKey="liste-numerotation-sculpture"
                name={`sculptureEditionNumerotation.${lang}`}
                label={t("sculpture.form.sculptureEditionNumerotation")}
                lang={lang}
              />
              <ModifiableList
                listKey="liste-signature-sculpture"
                name={`sculptureEditionSignature.${lang}`}
                label={t("sculpture.form.sculptureEditionSignature")}
                lang={lang}
              />
              <ModifiableList
                listKey="liste-marque-tampon-sculpture"
                name={`sculptureEditionMarqueTampons.${lang}`}
                label={t("sculpture.form.sculptureEditionMarqueTampons")}
                lang={lang}
                corner={() => (
                  <AF.Switch
                    name={`sculptureEditionMarqueTamponsPublished`}
                    label={t(
                      "sculpture.form.sculptureEditionMarqueTamponsPublished",
                    )}
                  />
                )}
              />
            </AF.Col>
          )}
        />
      </AF.Col>

      <AF.Col className="w-1/2">
        <Accordion type="multiple" className="w-full mb-4 bg-white">
          <AccordionItem value="estimation" className="bg-white">
            <AccordionTrigger>
              {t("sculpture.form.sculptureEditionEstimationSeparatorTitle")}
            </AccordionTrigger>
            <AccordionContent>
              <AF.Text
                name="sculptureEditionEstimationDescription"
                label={t(
                  "sculpture.form.sculptureEditionEstimationDescription",
                )}
              />
              <AF.SelectCurrency
                name="sculptureEditionEstimationMontant"
                label={t("sculpture.form.sculptureEditionEstimationMontant")}
                className="w-full"
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="assurance" className="bg-white">
            <AccordionTrigger>
              {t("sculpture.form.sculptureEditionAssuranceSeparatorTitle")}
            </AccordionTrigger>
            <AccordionContent>
              <AF.Text
                name="sculptureEditionAssuranceDescription"
                label={t("sculpture.form.sculptureEditionAssuranceDescription")}
              />
              <AF.SelectCurrency
                name="sculptureEditionAssuranceMontant"
                label={t("sculpture.form.sculptureEditionAssuranceMontant")}
                className="w-full"
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="vente" className="bg-white">
            <AccordionTrigger>
              {t("sculpture.form.sculptureEditionVenteSeparatorTitle")}
            </AccordionTrigger>
            <AccordionContent>
              <AF.Text
                name="sculptureEditionVenteDescription"
                label={t("sculpture.form.sculptureEditionVenteDescription")}
              />
              <AF.SelectCurrency
                name="sculptureEditionVenteMontant"
                label={t("sculpture.form.sculptureEditionVenteMontant")}
                className="w-full"
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="acquisition" className="bg-white">
            <AccordionTrigger>
              {t("sculpture.form.sculptureEditionAcquisitionSeparatorTitle")}
            </AccordionTrigger>
            <AccordionContent>
              <AF.Text
                name="sculptureEditionAcquisitionDescription"
                label={t(
                  "sculpture.form.sculptureEditionAcquisitionDescription",
                )}
              />
              <AF.SelectCurrency
                name="sculptureEditionAcquisitionMontant"
                label={t("sculpture.form.sculptureEditionAcquisitionMontant")}
                className="w-full"
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <AF.RichText
          name="sculptureEditionNotes"
          label={t("sculpture.form.sculptureEditionNotes")}
        />

        <SeparatorWithText
          text={t("sculpture.form.sculptureEditionStorageSeparatorTitle")}
          className="my-4"
        />
        <ModifiableList
          listKey="liste-localisation"
          name="sculptureEditionLocation1"
          label={t("sculpture.form.sculptureEditionLocation1")}
        />
        <ModifiableList
          listKey="liste-localisation"
          name="sculptureEditionLocation2"
          label={t("sculpture.form.sculptureEditionLocation2")}
        />
        <ModifiableList
          listKey="liste-localisation"
          name="sculptureEditionLocation3"
          label={t("sculpture.form.sculptureEditionLocation3")}
        />
      </AF.Col>
    </AF.Row>
  );
};

export default SculptureEditionInformationsTab;
